import { Fullscript } from "@fullscript/fullscript-js";
import type { FullscriptEnv } from "@fullscript/fullscript-js/dist/fullscript";
import { ExpectedActionError } from "@/lib/errors";
import { createLogger } from "@/lib/logger";
import type { Member } from "@/models/member/Member";
import {
  type FullscriptErrorSummary,
  readMissingScope,
  summarizeFullscriptErrorBody,
} from "./error-summary";
import { patchFullscriptDemographics } from "./patch-patient-demographics";
import {
  buildFullscriptPatientDemographics,
  type FullscriptPatientDemographics,
} from "./patient-demographics";
import {
  decideBirthDateLink,
  matchSearchPatientRow,
  pickCandidateByBirthDate,
  type SearchPatientPerson,
} from "./search-patient-match";
import type {
  FullscriptPatient,
  FullscriptRecommendation,
  FullscriptTreatmentPlan,
  FullscriptVariantResponse,
} from "./types";
import { FULLSCRIPT_TREATMENT_PLAN_STATE_ACTIVE, FullscriptTreatmentPlanSchema } from "./types";

const logger = createLogger("fullscript-client");

// cancel statuses that cancelFullscriptTreatmentPlan may resolve as "already closed"
const EXPECTED_CANCEL_FAILURE_STATUSES = [404, 410, 422];

/**
 * Custom error class for when a patient already exists in Fullscript
 */
export class FullscriptPatientExistsError extends Error {
  public readonly statusCode: number = 422;
  public readonly email: string;

  constructor(email: string) {
    super("PATIENT_ALREADY_EXISTS");
    this.name = "FullscriptPatientExistsError";
    this.email = email;

    // Maintains proper stack trace for where the error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FullscriptPatientExistsError);
    }
  }
}

interface FullscriptApiErrorDetails {
  responseBody?: string;
  summary?: FullscriptErrorSummary;
  /** The OAuth scope that a 403 body names as missing. See `readMissingScope`. */
  missingScope?: string;
}

/**
 * Error class for Fullscript API responses with non-2xx status codes.
 */
export class FullscriptApiError extends Error {
  public readonly status: number;
  public readonly statusCode: number;
  public readonly responseBody?: string;
  /** PHI-free classification of the error body, safe to log. */
  public readonly summary?: FullscriptErrorSummary;
  /** The OAuth scope that a 403 body names as missing, e.g. `catalog:read`. Safe to log. */
  public readonly missingScope?: string;

  constructor(
    message: string,
    status: number,
    { responseBody, summary, missingScope }: FullscriptApiErrorDetails = {},
  ) {
    super(message);
    this.name = "FullscriptApiError";
    this.status = status;
    this.statusCode = status;
    this.responseBody = responseBody;
    this.summary = summary;
    this.missingScope = missingScope;
  }
}

/**
 * The provider has no usable Fullscript connection for this request. The type lets telemetry
 * bucket the failure as `unlinked` without a match on message text. The provider fixes it by
 * connecting Fullscript, so it is an `ExpectedActionError`: `createServerAction` logs it and runs
 * `onError` (`fs_error`), but does not send it to Sentry.
 */
export class FullscriptUnlinkedError extends ExpectedActionError {
  name = "FullscriptUnlinkedError";
}

/**
 * Returns true when Fullscript says a resource is already missing or gone.
 */
export function isFullscriptGoneError(error: unknown): error is FullscriptApiError {
  return (
    error instanceof FullscriptApiError && (error.statusCode === 404 || error.statusCode === 410)
  );
}

export class FullscriptClient {
  constructor(private accessToken: string) {
    this.accessToken = accessToken;
  }

  async createPatient(member: Member): Promise<{ patient: FullscriptPatient }> {
    logger.info("Starting createPatient", {
      memberId: member.id,
    });

    if (!this.accessToken) {
      logger.error("No access token found for createPatient", {
        memberId: member.id,
      });
      throw new Error("No access token found");
    }

    const firstName = member.firstName ?? "";
    const lastName = member.lastName ?? "";
    const email = member.email ?? "";

    const requestBody = {
      first_name: firstName,
      last_name: lastName,
      email,
    };

    const requestUrl = `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/patients`;

    logger.info("Preparing createPatient request", {
      memberId: member.id,
      requestUrl,
      hasAccessToken: !!this.accessToken,
    });

    const response = await fetch(requestUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.accessToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    logger.info("Received response from Fullscript createPatient", {
      memberId: member.id,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throwIfScopeMissing({
        response,
        errorText,
        message: `Error creating patient: ${response.status}`,
        meta: { memberId: member.id },
      });
      // never log the raw body - Fullscript can echo submitted values back
      const errorSummary = summarizeFullscriptErrorBody(errorText);
      logger.error("Error response from Fullscript createPatient", {
        memberId: member.id,
        status: response.status,
        statusText: response.statusText,
        ...errorSummary,
        requestUrl,
      });

      // 422 + "Email has already been taken" = the patient exists; the caller links it by email
      if (response.status === 422 && errorSummary.isEmailTaken) {
        logger.info("Confirmed duplicate patient error, throwing FullscriptPatientExistsError", {
          memberId: member.id,
        });
        throw new FullscriptPatientExistsError(email);
      }

      logger.error("Throwing generic error for non-duplicate patient error", {
        memberId: member.id,
        status: response.status,
        messageCount: errorSummary.messageCount,
        isEmailTaken: errorSummary.isEmailTaken,
      });
      throw new FullscriptApiError(`Error creating patient: ${response.status}`, response.status);
    }

    const data = await response.json();
    logger.info("Successfully created patient", {
      memberId: member.id,
      // `data` is the Fullscript patient object (name, email, DOB) — log its
      // shape, never its contents.
      responseKeys: Object.keys(data ?? {}),
      hasPatientProperty: !!data.patient,
      fullscriptPatientId: data.patient?.id,
    });

    // Separate from the POST: a value Fullscript rejects must not fail the create and the link.
    // A new patient has none of these fields, so every valid one is sent.
    if (data.patient?.id) {
      const sentFields = await patchFullscriptDemographics({
        client: this,
        fullscriptPatientId: data.patient.id,
        demographics: buildFullscriptPatientDemographics(member),
        memberId: member.id,
        practiceId: null,
      });
      // field names only - the values are PHI
      logger.info("Sent Fullscript patient demographics after create", {
        memberId: member.id,
        sentFields,
      });
    }

    return data;
  }

  /**
   * Finds the Fullscript patient for `person` by email. A dependent can share the guardian's email,
   * so the row is picked by `matchSearchPatientRow`, which also compares the person's name. A
   * picked row is then read once to compare the date of birth (`confirmSearchMatchBirthDate`).
   */
  async searchPatientByEmail(
    email: string,
    person: SearchPatientPerson,
  ): Promise<{ patient: FullscriptPatient } | null> {
    logger.info("Starting searchPatientByEmail");

    if (!this.accessToken) {
      logger.error("No access token found for searchPatientByEmail");
      throw new Error("No access token found");
    }

    // patient_type=all - without it Fullscript leaves out every patient that has dependents
    const requestUrl = `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/search/patients?query=${encodeURIComponent(email)}&patient_type=all`;
    // The search URL carries the patient's email in its query string, so only the path is safe
    // to log. Every other request URL in this client is id-based and logged whole.
    const loggableUrl = `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/search/patients`;
    logger.info("Making request to Fullscript API", {
      url: loggableUrl,
      hasAccessToken: !!this.accessToken,
    });

    // Note: This endpoint assumes Fullscript provides a patient search API
    // The exact endpoint may need to be adjusted based on Fullscript's API documentation
    // If this endpoint doesn't exist, you may need to implement a different approach
    // such as storing patient emails in your system and looking them up locally first
    const response = await fetch(requestUrl, {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
      },
    });

    logger.info("Received response from Fullscript API", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.info("No patient found with email (404 response)", {
          status: response.status,
        });
        return null;
      }

      const errorText = await response.text();
      throwIfScopeMissing({
        response,
        errorText,
        message: `Error searching for patient: ${response.status}`,
        meta: {},
      });
      logger.error("Error response from Fullscript searchPatientByEmail", {
        status: response.status,
        statusText: response.statusText,
        ...summarizeFullscriptErrorBody(errorText),
        requestUrl: loggableUrl,
      });
      throw new FullscriptApiError(
        `Error searching for patient: ${response.status}`,
        response.status,
      );
    }

    const data = await response.json();
    logger.info("Successfully parsed response data", {
      hasPatientProperty: !!data.patient,
      hasPatientsArray: !!(data.patients && Array.isArray(data.patients)),
      patientsArrayLength: data.patients ? data.patients.length : 0,
      hasIdProperty: !!data.id,
      responseKeys: Object.keys(data),
    });

    // Handle different possible response formats
    if (data.patient) {
      logger.info("Found patient in response.patient property", {
        fullscriptPatientId: data.patient.id,
      });
      return { patient: data.patient };
    } else if (data.patients && Array.isArray(data.patients) && data.patients.length > 0) {
      const match = matchSearchPatientRow({ rows: data.patients, email, person });
      if (match.skippedIssuePaths.length) {
        // paths only - the skipped rows have a different email, so they cannot be the member
        logger.warn("Skipped malformed Fullscript patient search rows", {
          arrayLength: data.patients.length,
          skippedRowIssueCount: match.skippedIssuePaths.length,
          skippedIssuePaths: match.skippedIssuePaths,
        });
      }
      if (match.kind === "match") {
        const matchingPatient: FullscriptPatient = data.patients[match.index];
        logger.info("Found matching patient in response.patients array", {
          arrayLength: data.patients.length,
          matchingPatientId: matchingPatient.id,
        });
        const isConfirmed = await this.confirmSearchMatchBirthDate({
          fullscriptPatientId: matchingPatient.id,
          person,
          isSoleEmailRow: match.isSoleEmailRow,
          isExactName: match.isExactName,
        });
        return isConfirmed ? { patient: matchingPatient } : null;
      }
      if (match.kind === "name-candidates") {
        const index = await this.pickNameCandidateByBirthDate({
          indices: match.indices,
          candidatePatientIds: match.candidatePatientIds,
          rows: data.patients,
          person,
        });
        return index === undefined ? null : { patient: data.patients[index] };
      }
      if (match.kind === "ambiguous") {
        // ids only - names and email are PHI. A dependent can carry the guardian's email.
        logger.warn("Email matches no single Fullscript patient for the member, not linking", {
          arrayLength: data.patients.length,
          candidateFullscriptPatientIds: match.candidatePatientIds,
          dependentCount: match.dependentCount,
        });
        return null;
      }
      if (match.kind === "invalid-rows") {
        logger.warn("Fullscript patient search rows failed validation", {
          arrayLength: data.patients.length,
          issuePaths: match.issuePaths,
        });
        return null;
      }
      logger.warn("No patient with matching email found in patients array", {
        arrayLength: data.patients.length,
      });
      return null;
    } else if (data.id) {
      // If the response is the patient object directly
      logger.info("Found patient as direct response object", {
        fullscriptPatientId: data.id,
      });
      return { patient: data };
    }

    logger.warn("No patient found in response despite 200 status", {
      responseKeys: Object.keys(data),
    });

    return null;
  }

  /**
   * The search rows have no date of birth, and a parent and a child can share a name and an email.
   * So before a name-matched row is linked, read the patient and let `decideBirthDateLink` compare
   * the dates of birth. A failed read gives no link (fail closed). Logs carry ids only.
   */
  private async confirmSearchMatchBirthDate({
    fullscriptPatientId,
    person,
    isSoleEmailRow,
    isExactName,
  }: {
    fullscriptPatientId: string;
    person: SearchPatientPerson;
    isSoleEmailRow: boolean;
    isExactName: boolean;
  }): Promise<boolean> {
    let fullscriptPatient: FullscriptPatient | undefined;
    try {
      fullscriptPatient = (await this.getPatient(fullscriptPatientId))?.patient;
    } catch (error) {
      // 403 here, as in findReadableFullscriptPatientId, means "not this account" - same result
      logger.warn("Could not read the matched Fullscript patient, not linking", {
        fullscriptPatientId,
        status: error instanceof FullscriptApiError ? error.status : null,
      });
      return false;
    }
    if (!fullscriptPatient) {
      logger.warn("Matched Fullscript patient was not found, not linking", { fullscriptPatientId });
      return false;
    }

    const { shouldLink, reason } = decideBirthDateLink({
      fullscriptPatient,
      person,
      isSoleEmailRow,
      isExactName,
    });
    if (!shouldLink) {
      logger.warn("Matched Fullscript patient failed the date-of-birth check, not linking", {
        fullscriptPatientId,
        reason,
      });
      return false;
    }
    logger.info("Matched Fullscript patient passed the date-of-birth check", {
      fullscriptPatientId,
      reason,
    });
    return true;
  }

  /**
   * Several search rows have the person's name, for example a parent "John Smith" and a child
   * "John Smith Jr" on one email. Reads each candidate and returns the row index of the one whose
   * date of birth equals the person's. Any failed read gives no link (fail closed). Logs carry ids only.
   */
  private async pickNameCandidateByBirthDate({
    indices,
    candidatePatientIds,
    rows,
    person,
  }: {
    indices: number[];
    candidatePatientIds: string[];
    rows: FullscriptPatient[];
    person: SearchPatientPerson;
  }): Promise<number | undefined> {
    const candidates: { index: number; fullscriptPatient: unknown }[] = [];
    for (const index of indices) {
      const fullscriptPatientId = rows[index].id;
      try {
        const fullscriptPatient = (await this.getPatient(fullscriptPatientId))?.patient;
        if (!fullscriptPatient) throw new Error("Fullscript patient not found");
        candidates.push({ index, fullscriptPatient });
      } catch (error) {
        logger.warn("Could not read a Fullscript name candidate, not linking", {
          fullscriptPatientId,
          candidateFullscriptPatientIds: candidatePatientIds,
          status: error instanceof FullscriptApiError ? error.status : null,
        });
        return undefined;
      }
    }

    const pick = pickCandidateByBirthDate({ candidates, person });
    if ("reason" in pick) {
      logger.warn("No single Fullscript name candidate has the member's date of birth", {
        candidateFullscriptPatientIds: candidatePatientIds,
        reason: pick.reason,
      });
      return undefined;
    }
    logger.info("Picked a Fullscript name candidate by date of birth", {
      fullscriptPatientId: rows[pick.index].id,
      candidateFullscriptPatientIds: candidatePatientIds,
    });
    return pick.index;
  }

  async getPatient(patientId: string): Promise<{ patient: FullscriptPatient } | null> {
    if (!this.accessToken) {
      logger.error("No access token found");
      throw new Error("No access token found");
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/patients/${patientId}`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        logger.info("Patient not found", { fullscriptPatientId: patientId });
        return null;
      }

      const errorText = await response.text();
      const message = `Error getting patient: ${response.status}`;
      throwIfScopeMissing({
        response,
        errorText,
        message,
        meta: { fullscriptPatientId: patientId },
      });
      logger.error(message, {
        ...summarizeFullscriptErrorBody(errorText),
        fullscriptPatientId: patientId,
      });
      throw new FullscriptApiError(message, response.status);
    }

    const data = await response.json();
    return { patient: data.patient || data };
  }

  async updatePatient(
    patientId: string,
    updates: FullscriptPatientDemographics & {
      email?: string;
    },
    options?: { signal?: AbortSignal },
  ): Promise<{ patient: FullscriptPatient }> {
    if (!this.accessToken) {
      logger.error("No access token found");
      throw new Error("No access token found");
    }

    const requestUrl = `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/patients/${patientId}`;
    const response = await fetch(requestUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.accessToken}`,
      },
      body: JSON.stringify(updates),
      signal: options?.signal,
    });

    // No log here: the caller decides. A demographics backfill that Fullscript rejects on every
    // draft save must stay out of Sentry, and the email fanout logs its own failures.
    // The error keeps a PHI-free summary, not the body: Fullscript can echo the submitted values.
    if (!response.ok) {
      throw new FullscriptApiError(
        `Error updating patient: ${response.status} ${response.statusText}`,
        response.status,
        { summary: summarizeFullscriptErrorBody(await response.text()) },
      );
    }

    const data = await response.json();
    return { patient: data.patient || data };
  }

  async getProductVariantInformation(productId: string): Promise<FullscriptVariantResponse> {
    if (!this.accessToken) {
      logger.error("No access token found");
      throw new Error("No access token found");
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/catalog/variants/${productId}`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      },
    );

    if (!response.ok) {
      logger.error("Failed to get product variant information", {
        status: response.status,
        statusText: response.statusText,
      });

      throw new FullscriptApiError("Failed to get product variant information", response.status);
    }

    const data = await response.json();

    return data;
  }

  /**
   * Reads one treatment plan. A 404/410 returns null; any other failure throws `FullscriptApiError`.
   * `isForbiddenExpected` is for a caller that handles a 403 itself: the 403 then logs at warn
   * and stays out of Sentry. Every other caller keeps the error log for a 403. `signal` stops a
   * read that a caller cannot wait on.
   */
  async getTreatmentPlan(
    treatmentPlanId: string,
    options?: { isForbiddenExpected: boolean; signal?: AbortSignal },
  ): Promise<FullscriptTreatmentPlan | null> {
    if (!this.accessToken) {
      throw new Error("No access token found");
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/treatment_plans/${treatmentPlanId}`,
      {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
        signal: options?.signal,
      },
    );

    if (!response.ok) {
      if (response.status === 404 || response.status === 410) {
        logger.info("Treatment plan not found or already cancelled", {
          treatmentPlanId,
          status: response.status,
        });
        return null;
      }

      // never log the raw body - a plan error can echo the patient's name or email
      const errorText = await response.text();
      const errorMeta = {
        status: response.status,
        statusText: response.statusText,
        ...summarizeFullscriptErrorBody(errorText),
        treatmentPlanId,
      };
      if (response.status === 403 && options?.isForbiddenExpected) {
        // out of Sentry: the caller handles a 403 as an expected outcome
        logger.warn("Treatment plan read forbidden", errorMeta, { mirrorToSentry: false });
      } else {
        logger.error("Error getting treatment plan", errorMeta);
      }
      throw new FullscriptApiError(
        `Error getting treatment plan: ${response.status}`,
        response.status,
      );
    }

    const data = await response.json();
    return parseTreatmentPlanResponse(data.treatment_plan ?? data, {
      treatmentPlanId,
      operation: "getTreatmentPlan",
    });
  }

  /**
   * Lists a patient's active Fullscript treatment plans, walking every page.
   *
   * Fullscript's index endpoint has no server-side status filter, so we fetch
   * all plans and keep only the active ones. Pagination follows the `meta.next_page`
   * cursor (page[size] max is 100).
   *
   * Returns an empty list (not an error) when the patient has no plans.
   */
  async listTreatmentPlans(patientId: string): Promise<FullscriptTreatmentPlan[]> {
    if (!this.accessToken) {
      throw new Error("No access token found");
    }

    const pageSize = 100; // Fullscript's documented maximum page size.
    const plans: FullscriptTreatmentPlan[] = [];
    let page = 1;

    while (true) {
      const params = new URLSearchParams({
        sort_by: "created_at",
        order_by: "DESC",
        "page[number]": String(page),
        "page[size]": String(pageSize),
      });
      const requestUrl = `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/patients/${patientId}/treatment_plans?${params}`;

      const response = await fetch(requestUrl, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404 || response.status === 410) {
          logger.info("Treatment plan pagination ended without a readable next page", {
            fullscriptPatientId: patientId,
            accumulatedPlans: plans.length,
            page,
            status: response.status,
          });
          break;
        }

        // never log the raw body - a plan error can echo the patient's name or email
        const errorText = await response.text();
        logger.error("Error listing treatment plans", {
          status: response.status,
          statusText: response.statusText,
          ...summarizeFullscriptErrorBody(errorText),
          fullscriptPatientId: patientId,
        });
        throw new FullscriptApiError(
          `Error listing treatment plans: ${response.status}`,
          response.status,
        );
      }

      const data = await response.json();
      // Production API wraps plans under `treatment_plans`; the fallbacks are defensive.
      // Deliberately not parsed: this index endpoint returns summaries, and callers read only
      // `id` and `state` off them before re-fetching each plan with `getTreatmentPlan`. Requiring
      // the full plan shape here would drop every summary and silently break the patient sync.
      const pagePlans: FullscriptTreatmentPlan[] = data.treatment_plans ?? data.plans ?? [];
      plans.push(...pagePlans);

      const nextPage: number | null | undefined = data.meta?.next_page;
      if (!nextPage) {
        break;
      }
      page = nextPage;
    }

    // No server-side state filter exists, so keep only active plans here.
    return plans.filter((plan) => plan.state === FULLSCRIPT_TREATMENT_PLAN_STATE_ACTIVE);
  }

  /**
   * Replaces a plan's product list. `personalMessage`, when given, also replaces the plan's message
   * to the patient; without it the message stays as it is.
   */
  async updateTreatmentPlan(
    treatmentPlanId: string,
    recommendations: FullscriptRecommendation[],
    options?: { personalMessage?: string },
  ) {
    if (!this.accessToken) {
      throw new Error("No access token found");
    }

    // Map back to the PATCH endpoint's bare field names.
    // Inbound dosage always reaches us as `recommended_*`, whichever surface it came from —
    // `FullscriptDosageSchema` normalizes the REST response's `amount`/`frequency`/`duration`
    // onto those names. PATCH wants the bare ones, so convert here and nowhere else.
    //
    // Getting this wrong loses clinical data on Fullscript's side, not just ours: the
    // delete-supplement flow GETs the plan, drops one recommendation, and PATCHes the rest back.
    // An unmapped `amount` serializes away as `undefined`, so every surviving recommendation
    // would be pushed with no dose, frequency, or duration.
    const patchRecommendations = recommendations.map((rec) => ({
      variant_id: rec.variant_id,
      units_to_purchase: rec.units_to_purchase,
      refill: rec.refill,
      take_with: rec.take_with,
      dosage: rec.dosage
        ? {
            amount: rec.dosage.recommended_amount,
            frequency: rec.dosage.recommended_frequency,
            duration: rec.dosage.recommended_duration,
            additional_info: rec.dosage.additional_info,
            format: rec.dosage.format,
            time_of_day: rec.dosage.time_of_day,
          }
        : undefined,
    }));

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/treatment_plans/${treatmentPlanId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify({
          recommendations: patchRecommendations,
          ...(options?.personalMessage !== undefined
            ? { personal_message: options.personalMessage }
            : {}),
        }),
      },
    );

    if (!response.ok) {
      // never log the raw body - a plan error can echo the patient's name or email
      logger.error("Failed to update treatment plan", {
        status: response.status,
        statusText: response.statusText,
        ...summarizeFullscriptErrorBody(await response.text()),
        treatmentPlanId,
      });
      throw new FullscriptApiError(
        `Failed to update treatment plan: ${response.status}`,
        response.status,
      );
    }

    return response.json();
  }

  async cancelTreatmentPlan(treatmentPlanId: string) {
    if (!this.accessToken) {
      throw new Error("No access token found");
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_FULLSCRIPT_API_SERVER}/clinic/treatment_plans/${treatmentPlanId}/cancel`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.accessToken}`,
        },
      },
    );

    if (!response.ok) {
      const logMeta = {
        status: response.status,
        statusText: response.statusText,
        treatmentPlanId,
      };
      // 404/410/422 can mean the plan is already closed (422 = already cancelled) - the caller
      // decides, so keep these out of Sentry
      if (EXPECTED_CANCEL_FAILURE_STATUSES.includes(response.status)) {
        logger.warn("Fullscript refused treatment plan cancel", logMeta, {
          mirrorToSentry: false,
        });
      } else {
        logger.error("Failed to cancel treatment plan", logMeta);
      }
      throw new FullscriptApiError(
        `Failed to cancel treatment plan: ${response.status}`,
        response.status,
      );
    }

    return response.json();
  }

  /**
   * Returns the URL to start a Fullscript OAuth connection.
   *
   * The client points the browser at our own `/api/fullscript/connect` route,
   * which authenticates the caller server-side, captures a random nonce in an
   * httpOnly cookie, and only then redirects out to Fullscript. The providerId
   * is intentionally NOT placed in the URL — the connect route derives it from
   * the authenticated Practitioner profile so callers cannot link credentials
   * to an arbitrary provider.
   *
   * @param _providerId - Unused; retained for call-site compatibility. The
   *   server resolves the provider from the session.
   * @param returnUrl - Where to send the user after a successful connection.
   */
  static getConnectionURL(_providerId: string, returnUrl?: string) {
    const params = new URLSearchParams();
    if (returnUrl) {
      params.set("returnUrl", returnUrl);
    }
    const qs = params.toString();
    return qs ? `/api/fullscript/connect?${qs}` : `/api/fullscript/connect`;
  }

  static initializeEmbed() {
    const publicKey = process.env.NEXT_PUBLIC_FULLSCRIPT_CLIENT_ID;
    const env = process.env.NEXT_PUBLIC_FULLSCRIPT_ENVIRONMENT;

    if (!publicKey) {
      logger.error("No public key found");
      throw new Error("No public key found");
    }

    if (!env) {
      logger.error("No environment found");
      throw new Error("No environment found");
    }

    const validEnvs: FullscriptEnv[] = ["us", "ca", "ca-snd", "us-snd", "dev"];

    if (!validEnvs.includes(env as FullscriptEnv)) {
      logger.error(`Invalid environment: ${env}. Must be one of: ${validEnvs.join(", ")}`);
      throw new Error(`Invalid environment: ${env}`);
    }

    const fullscriptClient = Fullscript({
      publicKey,
      env: env as FullscriptEnv,
    });

    return fullscriptClient;
  }
}

/**
 * Validates a plan from a REST response instead of casting it.
 *
 * The cast this replaces is what let the dosage key mismatch through: the response type declared
 * `recommended_amount`, the API sends `amount`, and TypeScript cannot see a difference it was
 * never shown. `FullscriptDosageSchema` reconciles the two.
 *
 * Returns null rather than throwing. A plan we cannot read is a reason to stop and report, not to
 * fail a webhook delivery that Fullscript would then retry — every caller already handles null.
 */
function parseTreatmentPlanResponse(
  raw: unknown,
  context: Record<string, unknown>,
): FullscriptTreatmentPlan | null {
  const parsed = FullscriptTreatmentPlanSchema.safeParse(raw);

  if (!parsed.success) {
    logger.error("Fullscript treatment plan response did not match the expected shape", {
      ...context,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join("."),
        code: issue.code,
        message: issue.message,
      })),
    });
    return null;
  }

  return parsed.data as FullscriptTreatmentPlan;
}

export const createFullscriptClient = (accessToken: string) => {
  return new FullscriptClient(accessToken);
};

/**
 * Throws a `FullscriptApiError` with `missingScope` for a 403 whose body names a missing OAuth
 * scope. Logs at warn with no Sentry event: a reconnect or a dashboard scope grant fixes it, not
 * our code. Returns for any other response, so the caller logs and throws as before.
 */
function throwIfScopeMissing({
  response,
  errorText,
  message,
  meta,
}: {
  response: Response;
  errorText: string;
  message: string;
  meta: Record<string, unknown>;
}) {
  const missingScope = response.status === 403 ? readMissingScope(errorText) : undefined;
  if (!missingScope) return;
  logger.warn(message, { ...meta, missingScope }, { mirrorToSentry: false });
  throw new FullscriptApiError(message, response.status, { missingScope });
}
